import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { Header } from "@/components/layout/Header";
import { StepIndicator } from "@/components/layout/StepIndicator";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { decodeFiscalCode, calculateAge, formatDateItalian } from "@/utils/fiscalCode";
import { 
  startScanning,
  stopScanning,
  extractCodiceFiscaleFromBarcode, 
  isCameraSupported,
  cleanup as cleanupBarcodeReader
} from "@/services/barcodeReader";
import { 
  CreditCard, 
  Users, 
  Search, 
  Calendar, 
  Clock, 
  User, 
  ChevronRight,
  Camera,
  CheckCircle2,
  UserPlus,
  AlertCircle,
  MapPin,
  Cake,
  Hash,
  Loader2,
  X,
  AlertTriangle,
  ScanBarcode
} from "lucide-react";

// Mock data for patients list (from booking system)
const mockPatients = [
  {
    id: "1",
    name: "Giuseppe Verdi",
    fiscalCode: "VRDGPP80A01H501Z",
    dateOfBirth: "1980-01-01",
    phone: "+39 333 1234567",
    address: "Via Roma 10, Milano",
    appointmentTime: "09:00",
    services: ["Controllo Diabete"],
    gender: "M",
    birthPlace: "Roma"
  },
  {
    id: "2",
    name: "Maria Bianchi",
    fiscalCode: "BNCMRA75B42F205X",
    dateOfBirth: "1975-02-02",
    phone: "+39 334 7654321",
    address: "Via Dante 25, Milano",
    appointmentTime: "09:30",
    services: ["Emocromo", "Glicemia"],
    gender: "F",
    birthPlace: "Milano"
  },
  {
    id: "3",
    name: "Antonio Russo",
    fiscalCode: "RSSANT65C15L219K",
    dateOfBirth: "1965-03-15",
    phone: "+39 335 9876543",
    address: "Corso Magenta 8, Milano",
    appointmentTime: "10:00",
    services: ["Pressione Arteriosa"],
    gender: "M",
    birthPlace: "Torino"
  },
  {
    id: "4",
    name: "Francesca Esposito",
    fiscalCode: "SPSFNC90D55H501W",
    dateOfBirth: "1990-04-15",
    phone: "+39 336 1122334",
    address: "Via Torino 42, Milano",
    appointmentTime: "10:30",
    services: ["Ossimetria", "ECG"],
    gender: "F",
    birthPlace: "Roma"
  },
  {
    id: "5",
    name: "Luca Romano",
    fiscalCode: "RMNLCU85E20F839P",
    dateOfBirth: "1985-05-20",
    phone: "+39 337 4455667",
    address: "Piazza Duomo 1, Milano",
    appointmentTime: "11:00",
    services: ["Controllo Completo"],
    gender: "M",
    birthPlace: "Napoli"
  },
  {
    id: "6",
    name: "Mirko Giulianini",
    fiscalCode: "GLNMRK74A01C847K",
    dateOfBirth: "1974-01-01",
    phone: "+39 338 1234567",
    address: "Via Cesena 15, Cesena",
    appointmentTime: null,
    services: [],
    gender: "M",
    birthPlace: "Cesena"
  }
];

// Unique ID for the scanner element
const SCANNER_ELEMENT_ID = "barcode-scanner-container";

export const PatientSelectionPage = () => {
  const navigate = useNavigate();
  const { setSelectedPatient } = useAppContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("list");
  
  // Camera/Barcode state
  const [showCamera, setShowCamera] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState("Inquadra il codice a barre della tessera");
  const [scanError, setScanError] = useState(null);
  
  // Results state
  const [scannedData, setScannedData] = useState(null);
  const [decodedData, setDecodedData] = useState(null);
  const [existingPatient, setExistingPatient] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupBarcodeReader();
    };
  }, []);

  // Filter patients based on search
  const filteredPatients = mockPatients.filter(patient => 
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.fiscalCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    toast.success("Paziente selezionato", {
      description: patient.name
    });
    navigate("/services");
  };

  // Check if patient exists in local database by CF
  const checkPatientExists = (fiscalCode) => {
    if (!fiscalCode) return null;
    return mockPatients.find(p => 
      p.fiscalCode.toUpperCase() === fiscalCode.toUpperCase()
    );
  };

  // Handle successful barcode scan
  const handleBarcodeSuccess = useCallback((decodedText, decodedResult) => {
    console.log('Barcode scansionato:', decodedText);
    console.log('Formato rilevato:', decodedResult?.result?.format?.formatName || 'sconosciuto');
    
    // Stop scanning
    stopScanning();
    setIsScanning(false);
    setShowCamera(false);
    
    // Extract codice fiscale
    const cfResult = extractCodiceFiscaleFromBarcode(decodedText);
    
    console.log('Risultato estrazione CF:', cfResult);
    
    // Se è un oggetto con type, gestisci i vari casi
    if (cfResult && typeof cfResult === 'object') {
      if (cfResult.type === 'tessera') {
        toast.info("Numero tessera rilevato", {
          description: `Tessera: ${cfResult.value.slice(0, 4)}...${cfResult.value.slice(-4)}`
        });
        
        setScannedData({
          cognome: "",
          nome: "",
          codiceFiscale: "",
          numeroTessera: cfResult.value,
          scadenza: ""
        });
        setDecodedData(null);
        setExistingPatient(null);
        setShowResultDialog(true);
        
      } else {
        // type === 'unknown' - mostra il valore e permetti inserimento manuale
        toast.warning("Codice non riconosciuto", {
          description: `Valore letto: ${cfResult.value.slice(0, 20)}${cfResult.value.length > 20 ? '...' : ''}`
        });
        
        // Se sembra un codice fiscale (16 caratteri alfanumerici), precompila
        const mayBeCF = cfResult.value.length === 16 && /^[A-Z0-9]{16}$/.test(cfResult.value);
        
        setScannedData({
          cognome: "",
          nome: "",
          codiceFiscale: mayBeCF ? cfResult.value : "",
          numeroTessera: mayBeCF ? "" : cfResult.value,
          scadenza: ""
        });
        setDecodedData(mayBeCF ? decodeFiscalCode(cfResult.value) : null);
        setExistingPatient(mayBeCF ? checkPatientExists(cfResult.value) : null);
        setShowResultDialog(true);
      }
      
    } else if (cfResult) {
      // È un codice fiscale valido (stringa)
      const codiceFiscale = cfResult;
      const decoded = decodeFiscalCode(codiceFiscale);
      const existing = checkPatientExists(codiceFiscale);
      
      setScannedData({
        cognome: decoded?.cognome || "",
        nome: decoded?.nome || "",
        codiceFiscale: codiceFiscale,
        numeroTessera: "",
        scadenza: ""
      });
      setDecodedData(decoded);
      setExistingPatient(existing);
      setShowResultDialog(true);
      
      if (existing) {
        toast.success("Paziente trovato!", {
          description: existing.name
        });
      } else {
        toast.success("Codice Fiscale rilevato", {
          description: codiceFiscale
        });
      }
    } else {
      // Nessun risultato - mostra errore
      toast.error("Impossibile leggere il codice", {
        description: "Riprova posizionando meglio la tessera"
      });
    }
  }, []);

  // Start camera for barcode scanning
  const startCamera = async () => {
    try {
      if (!isCameraSupported()) {
        toast.error("Fotocamera non supportata", {
          description: "Il tuo browser non supporta l'accesso alla fotocamera"
        });
        return;
      }
      
      setScanError(null);
      setShowCamera(true);
      setIsScanning(true);
      setScanMessage("Avvio fotocamera...");
      
      // Wait for the DOM element to be ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Start scanning
      await startScanning(
        SCANNER_ELEMENT_ID,
        handleBarcodeSuccess,
        (error) => {
          console.error('Errore scansione:', error);
          setScanError(error.message);
          toast.error("Errore scansione", {
            description: error.message
          });
        },
        {
          qrbox: { width: 320, height: 80 }, // Ottimizzato per barcode lineari
          aspectRatio: 1.777,
          fps: 15
        }
      );
      
      setScanMessage("Inquadra il codice a barre della tessera");
      
    } catch (error) {
      console.error('Errore avvio fotocamera:', error);
      setIsScanning(false);
      setScanError(error.message);
      toast.error("Impossibile accedere alla fotocamera", {
        description: error.message
      });
    }
  };

  // Stop camera
  const stopCamera = useCallback(async () => {
    await stopScanning();
    setShowCamera(false);
    setIsScanning(false);
    setScanError(null);
  }, []);

  // Add new patient to local database
  const handleAddNewPatient = () => {
    if (!scannedData) return;

    const newPatient = {
      id: scannedData.codiceFiscale || `new-${mockPatients.length + 1}`,
      name: scannedData.nome && scannedData.cognome 
        ? `${scannedData.nome} ${scannedData.cognome}` 
        : "Nuovo Paziente",
      fiscalCode: scannedData.codiceFiscale,
      dateOfBirth: decodedData?.dataNascita || "",
      gender: decodedData?.sesso || "",
      birthPlace: decodedData?.luogoNascita || "",
      ssnNumber: scannedData.numeroTessera,
      cardExpiry: scannedData.scadenza,
      phone: "",
      address: "",
      appointmentTime: null,
      services: [],
      isNew: true
    };

    mockPatients.push(newPatient);
    
    toast.success("Paziente aggiunto all'anagrafica", {
      description: newPatient.name
    });

    setSelectedPatient(newPatient);
    setShowResultDialog(false);
    resetScan();
    navigate("/services");
  };

  // Select existing patient
  const handleSelectExistingPatient = () => {
    if (!existingPatient) return;
    
    setSelectedPatient(existingPatient);
    toast.success("Paziente riconosciuto", {
      description: existingPatient.name
    });
    
    setShowResultDialog(false);
    resetScan();
    navigate("/services");
  };

  // Reset scan state
  const resetScan = () => {
    setScannedData(null);
    setDecodedData(null);
    setExistingPatient(null);
    setScanMessage("Inquadra il codice a barre della tessera");
    setScanError(null);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Selezione Paziente" />
      <StepIndicator currentStep={1} />
      
      <main className="flex-1 px-4 pb-6 md:px-6">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Page header */}
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Seleziona Paziente
            </h1>
            <p className="mt-1 text-muted-foreground">
              Scansiona il codice a barre della tessera sanitaria o seleziona dalla lista prenotazioni
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-12">
              <TabsTrigger value="scan" className="gap-2 text-sm md:text-base" data-testid="tab-scan">
                <CreditCard className="h-4 w-4" />
                Tessera Sanitaria
              </TabsTrigger>
              <TabsTrigger value="list" className="gap-2 text-sm md:text-base" data-testid="tab-list">
                <Users className="h-4 w-4" />
                Lista Prenotazioni
              </TabsTrigger>
            </TabsList>

            {/* Card scan tab */}
            <TabsContent value="scan" className="mt-6">
              <Card className="border-2 border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12 md:py-16">
                  {/* Idle state - show scan options */}
                  <div className="text-center space-y-6">
                    {/* Card illustration */}
                    <div className="mx-auto w-48 h-28 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl flex items-center justify-center border-2 border-dashed border-blue-500/30">
                      <ScanBarcode className="h-12 w-12 text-blue-500/50" />
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold">Scansiona Codice a Barre</h3>
                      <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
                        Inquadra il codice a barre presente sul retro della Tessera Sanitaria
                      </p>
                    </div>
                    
                    {/* Info badges */}
                    <div className="flex flex-wrap justify-center gap-2">
                      <Badge variant="outline" className="gap-1">
                        <Hash className="h-3 w-3" />
                        Codice Fiscale
                      </Badge>
                      <Badge variant="outline" className="gap-1">
                        <Cake className="h-3 w-3" />
                        Data di Nascita
                      </Badge>
                      <Badge variant="outline" className="gap-1">
                        <MapPin className="h-3 w-3" />
                        Luogo di Nascita
                      </Badge>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        variant="default"
                        size="lg"
                        onClick={startCamera}
                        className="gap-2"
                        data-testid="start-camera-btn"
                      >
                        <Camera className="h-5 w-5" />
                        Avvia Scansione
                      </Button>
                    </div>
                    
                    {/* Camera support warning */}
                    {!isCameraSupported() && (
                      <div className="flex items-center gap-2 text-warning text-sm">
                        <AlertTriangle className="h-4 w-4" />
                        <span>Fotocamera non disponibile nel tuo browser.</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Patient list tab */}
            <TabsContent value="list" className="mt-6 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome o codice fiscale..."
                  className="pl-10 h-12"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-input"
                />
              </div>

              {/* Today's date */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Prenotazioni di oggi: {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
              </div>

              {/* Patient list */}
              <ScrollArea className="h-[400px] md:h-[450px]">
                <div className="space-y-3 pr-4">
                  {filteredPatients.length === 0 ? (
                    <div className="text-center py-12">
                      <User className="mx-auto h-12 w-12 text-muted-foreground/50" />
                      <p className="mt-4 text-muted-foreground">Nessun paziente trovato</p>
                    </div>
                  ) : (
                    filteredPatients.map((patient, index) => (
                      <Card
                        key={patient.id}
                        className="cursor-pointer hover:border-primary/50 transition-all animate-fade-in-up"
                        style={{ animationDelay: `${index * 50}ms` }}
                        onClick={() => handleSelectPatient(patient)}
                        data-testid={`patient-card-${patient.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <User className="h-5 w-5 text-primary" />
                                </div>
                                <div className="min-w-0">
                                  <h3 className="font-semibold truncate">{patient.name}</h3>
                                  <p className="text-sm text-muted-foreground font-mono">
                                    {patient.fiscalCode}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="mt-3 flex flex-wrap gap-2">
                                {patient.appointmentTime && (
                                  <Badge variant="secondary" className="gap-1">
                                    <Clock className="h-3 w-3" />
                                    {patient.appointmentTime}
                                  </Badge>
                                )}
                                {patient.services.map((service, idx) => (
                                  <Badge key={idx} variant="outline" className="bg-primary/5">
                                    {service}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            
                            <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-2" />
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Camera Modal with html5-qrcode */}
      <Dialog open={showCamera} onOpenChange={(open) => {
        if (!open) stopCamera();
      }}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="flex items-center gap-2">
              <ScanBarcode className="h-5 w-5 text-primary" />
              Scansione Codice a Barre
            </DialogTitle>
            <DialogDescription>
              Posiziona il codice a barre della tessera sanitaria nell&apos;inquadratura
            </DialogDescription>
          </DialogHeader>
          
          {/* Scanner Container */}
          <div className="relative bg-black">
            <div 
              id={SCANNER_ELEMENT_ID} 
              className="w-full"
              style={{ minHeight: '300px' }}
            />
            
            {/* Status message */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full">
              <div className="flex items-center gap-2 text-white text-sm">
                {isScanning && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>{scanMessage}</span>
              </div>
            </div>
            
            {/* Error message */}
            {scanError && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-destructive/90 text-destructive-foreground px-4 py-2 rounded-lg text-sm">
                {scanError}
              </div>
            )}
          </div>
          
          <DialogFooter className="p-4">
            <Button
              variant="outline"
              onClick={stopCamera}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={(open) => {
        if (!open) {
          setShowResultDialog(false);
          resetScan();
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Dati Tessera Sanitaria
            </DialogTitle>
            <DialogDescription>
              Verifica e modifica i dati estratti dalla tessera
            </DialogDescription>
          </DialogHeader>
          
          {scannedData && (
            <div className="space-y-4">
              {/* Patient status */}
              {existingPatient ? (
                <div className="p-3 rounded-lg bg-success/10 border border-success/30">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-success" />
                    <p className="font-medium text-success text-sm">Paziente già in anagrafica</p>
                  </div>
                </div>
              ) : scannedData.codiceFiscale ? (
                <div className="p-3 rounded-lg bg-warning/10 border border-warning/30">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-warning" />
                    <p className="font-medium text-warning text-sm">Nuovo paziente - modifica i dati se necessario</p>
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    <p className="font-medium text-destructive text-sm">Compila manualmente i dati</p>
                  </div>
                </div>
              )}
              
              {/* Editable data fields */}
              <div className="grid gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground font-medium">Cognome *</label>
                    <Input
                      value={scannedData.cognome || ""}
                      onChange={(e) => setScannedData({...scannedData, cognome: e.target.value.toUpperCase()})}
                      placeholder="COGNOME"
                      className="uppercase"
                      data-testid="edit-cognome"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground font-medium">Nome *</label>
                    <Input
                      value={scannedData.nome || ""}
                      onChange={(e) => setScannedData({...scannedData, nome: e.target.value.toUpperCase()})}
                      placeholder="NOME"
                      className="uppercase"
                      data-testid="edit-nome"
                    />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground font-medium">Codice Fiscale *</label>
                  <Input
                    value={scannedData.codiceFiscale || ""}
                    onChange={(e) => {
                      const newCF = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
                      setScannedData({...scannedData, codiceFiscale: newCF});
                      // Re-check if patient exists
                      const existing = checkPatientExists(newCF);
                      setExistingPatient(existing);
                      // Re-decode CF
                      if (newCF.length === 16) {
                        setDecodedData(decodeFiscalCode(newCF));
                      }
                    }}
                    placeholder="ABCDEF12G34H567I"
                    className="font-mono uppercase"
                    maxLength={16}
                    data-testid="edit-cf"
                  />
                </div>
                
                {decodedData && (
                  <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-muted/50 border">
                    <div>
                      <p className="text-xs text-muted-foreground">Data di Nascita (da CF)</p>
                      <p className="font-medium text-sm">{formatDateItalian(decodedData.dataNascita)}</p>
                      <p className="text-xs text-muted-foreground">({calculateAge(decodedData.dataNascita)} anni)</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Sesso (da CF)</p>
                      <p className="font-medium text-sm">{decodedData.sessoEsteso}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">Luogo di Nascita (da CF)</p>
                      <p className="font-medium text-sm">{decodedData.luogoNascita} ({decodedData.provinciaNascita})</p>
                    </div>
                  </div>
                )}
                
                <Separator />
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground font-medium">Numero Tessera</label>
                    <Input
                      value={scannedData.numeroTessera || ""}
                      onChange={(e) => setScannedData({...scannedData, numeroTessera: e.target.value.replace(/[^0-9]/g, '')})}
                      placeholder="80380000..."
                      className="font-mono text-sm"
                      data-testid="edit-tessera"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground font-medium">Scadenza</label>
                    <Input
                      value={scannedData.scadenza || ""}
                      onChange={(e) => setScannedData({...scannedData, scadenza: e.target.value})}
                      placeholder="DD/MM/YYYY"
                      data-testid="edit-scadenza"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowResultDialog(false);
                resetScan();
              }}
            >
              Annulla
            </Button>
            
            {existingPatient ? (
              <Button
                onClick={handleSelectExistingPatient}
                className="gap-2"
                data-testid="select-existing-btn"
              >
                <User className="h-4 w-4" />
                Seleziona Paziente
              </Button>
            ) : (
              <Button
                onClick={handleAddNewPatient}
                disabled={!scannedData?.codiceFiscale || scannedData.codiceFiscale.length < 16}
                className="gap-2"
                data-testid="add-new-patient-btn"
              >
                <UserPlus className="h-4 w-4" />
                Aggiungi e Continua
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientSelectionPage;
